import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  Copy,
  X,
  Crown,
  HandHeart,
  Presentation,
  GraduationCap,
  Factory,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";

type CodeType =
  | "vip"
  | "volunteer"
  | "exhibitor"
  | "industryDay"
  | "workshop"
  | "tutorial"
  | "all_workshops"
  | "all_tutorials";

type Counts = {
  vip: number;
  volunteer: number;
  exhibitor: number;
  industryDay: number;
  workshop: string;
  tutorial: string;
  all_workshops: number;
  all_tutorials: number;
};

const accentTones: Record<
  string,
  { ring: string; pill: string; dot: string; chipBg: string; chipText: string; chipBorder: string }
> = {
  blue: {
    ring: "ring-blue-500/30",
    pill: "bg-blue-500/15 text-blue-300",
    dot: "bg-blue-400",
    chipBg: "bg-blue-500/15",
    chipText: "text-blue-200",
    chipBorder: "border-blue-500/30",
  },
  emerald: {
    ring: "ring-emerald-500/30",
    pill: "bg-emerald-500/15 text-emerald-300",
    dot: "bg-emerald-400",
    chipBg: "bg-emerald-500/15",
    chipText: "text-emerald-200",
    chipBorder: "border-emerald-500/30",
  },
  violet: {
    ring: "ring-violet-500/30",
    pill: "bg-violet-500/15 text-violet-300",
    dot: "bg-violet-400",
    chipBg: "bg-violet-500/15",
    chipText: "text-violet-200",
    chipBorder: "border-violet-500/30",
  },
  amber: {
    ring: "ring-amber-500/30",
    pill: "bg-amber-500/15 text-amber-300",
    dot: "bg-amber-400",
    chipBg: "bg-amber-500/15",
    chipText: "text-amber-200",
    chipBorder: "border-amber-500/30",
  },
  rose: {
    ring: "ring-rose-500/30",
    pill: "bg-rose-500/15 text-rose-300",
    dot: "bg-rose-400",
    chipBg: "bg-rose-500/15",
    chipText: "text-rose-200",
    chipBorder: "border-rose-500/30",
  },
  cyan: {
    ring: "ring-cyan-500/30",
    pill: "bg-cyan-500/15 text-cyan-300",
    dot: "bg-cyan-400",
    chipBg: "bg-cyan-500/15",
    chipText: "text-cyan-200",
    chipBorder: "border-cyan-500/30",
  },
};

const CodesModal = ({
  isOpen,
  onClose,
  codes,
}: {
  isOpen: boolean;
  onClose: () => void;
  codes: string[];
}) => {
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleCopy = (code: string, index: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1200);
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 1200);
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/40">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-400" />
            <h2 className="text-sm font-semibold text-slate-100">
              Generated codes
              <span className="ml-2 text-xs text-slate-400">({codes.length})</span>
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleCopyAll}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
            >
              <Copy className="h-3 w-3" />
              {copiedAll ? "Copied" : "Copy all"}
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/40 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          <div
            className={`grid gap-2 ${
              codes.length > 15 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1"
            }`}
          >
            {codes.map((code, index) => (
              <div
                key={index}
                className="group bg-slate-800/60 border border-slate-700/40 rounded-lg px-3 py-2 flex justify-between items-center gap-2 hover:border-slate-600/60 transition-colors"
              >
                <span className="font-mono text-xs text-slate-100 truncate">{code}</span>
                <button
                  onClick={() => handleCopy(code, index)}
                  className="inline-flex items-center justify-center h-6 w-6 rounded text-slate-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
                  title="Copy"
                >
                  {copiedIndex === index ? (
                    <span className="text-[10px] text-emerald-300">✓</span>
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact dark "count" select used inside the cards
const CountSelect = ({
  value,
  onChange,
  options,
  accent,
}: {
  value: number;
  onChange: (n: number) => void;
  options: number[];
  accent: string;
}) => {
  const t = accentTones[accent];
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value))}
        className={`appearance-none bg-slate-900 border border-slate-700/60 rounded-md pl-3 pr-9 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-2 ${t.ring} focus:border-transparent`}
      >
        {options.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
    </div>
  );
};

const PerksList = ({ items }: { items: string[] }) => (
  <ul className="space-y-1 text-xs text-slate-300">
    {items.map((p, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1 h-1 w-1 rounded-full bg-slate-500 shrink-0" />
        <span>{p}</span>
      </li>
    ))}
  </ul>
);

type CardProps = {
  title: string;
  accent: keyof typeof accentTones;
  icon: React.ReactNode;
  children: React.ReactNode;
};
const Card = ({ title, accent, icon, children }: CardProps) => {
  const t = accentTones[accent];
  return (
    <section className="rounded-2xl border border-slate-700/40 bg-slate-800/40 overflow-hidden">
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-700/40 bg-slate-900/40">
        <span
          className={`inline-flex items-center justify-center h-7 w-7 rounded-md ${t.pill}`}
        >
          {icon}
        </span>
        <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
        <span className={`ml-auto h-2 w-2 rounded-full ${t.dot}`} />
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
};

const COUNT_OPTIONS = [1, 2, 3, 4, 5, 10, 25, 50];

export default function GiftCodeCreation() {
  const [counts, setCounts] = useState<Counts>({
    vip: 3,
    volunteer: 1,
    exhibitor: 1,
    industryDay: 1,
    workshop: "",
    tutorial: "",
    all_workshops: 1,
    all_tutorials: 1,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [workshops, setWorkshops] = useState<any[]>([]);
  const [tutorials, setTutorials] = useState<any[]>([]);
  const [busyType, setBusyType] = useState<CodeType | null>(null);

  const handleCountChange = (type: keyof Counts, value: number | string) => {
    setCounts((prev) => ({ ...prev, [type]: value }));
  };

  const handleCreate = async (type: CodeType) => {
    try {
      setBusyType(type);
      const response = await axios.post(`${base_url}/api/admin/create-codes`, {
        type,
        count: counts[type],
      });
      setGeneratedCodes(response.data.codes);
      setModalOpen(true);
    } catch (error) {
      console.error("Error creating codes:", error);
    } finally {
      setBusyType(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${base_url}/api/admin/ws-tut`);
        setWorkshops(response.data.workshops);
        setTutorials(response.data.tutorials);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const isBusy = (t: CodeType) => busyType === t;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title="VIP" accent="blue" icon={<Crown className="h-4 w-4" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PerksList
              items={[
                "Conference",
                "Workshops and Tutorials",
                "One opening entrance",
                "One Gala Dinner Seat",
              ]}
            />
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Create</span>
                <CountSelect
                  value={counts.vip}
                  onChange={(n) => handleCountChange("vip", n)}
                  options={COUNT_OPTIONS}
                  accent="blue"
                />
                <span className="text-slate-300">codes</span>
              </div>
              <button
                onClick={() => handleCreate("vip")}
                disabled={isBusy("vip")}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-blue-500/20 text-blue-200 border border-blue-500/30 hover:bg-blue-500/30 disabled:opacity-50 transition-colors"
              >
                {isBusy("vip") ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </Card>

        <Card title="Volunteer" accent="emerald" icon={<HandHeart className="h-4 w-4" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PerksList
              items={[
                "Conference",
                "Workshops and Tutorials",
                "Opening entrance",
                "Student Reception",
              ]}
            />
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Create</span>
                <CountSelect
                  value={counts.volunteer}
                  onChange={(n) => handleCountChange("volunteer", n)}
                  options={COUNT_OPTIONS}
                  accent="emerald"
                />
                <span className="text-slate-300">codes</span>
              </div>
              <button
                onClick={() => handleCreate("volunteer")}
                disabled={isBusy("volunteer")}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 disabled:opacity-50 transition-colors"
              >
                {isBusy("volunteer") ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </Card>

        <Card title="Workshop codes" accent="violet" icon={<GraduationCap className="h-4 w-4" />}>
          <p className="text-xs text-slate-400 mb-3">
            Codes that give access to a specific workshop.
          </p>
          <div className="flex flex-col gap-3">
            <div className="relative">
              <select
                className="w-full appearance-none bg-slate-900 border border-slate-700/60 rounded-md pl-3 pr-9 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                defaultValue=""
                onChange={(e) => handleCountChange("workshop", e.target.value)}
              >
                <option value="" disabled>
                  Select workshop…
                </option>
                {workshops.map((workshop: any) => (
                  <option key={workshop} value={workshop}>
                    Workshop {workshop}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleCreate("workshop")}
                disabled={isBusy("workshop") || !counts.workshop}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-violet-500/20 text-violet-200 border border-violet-500/30 hover:bg-violet-500/30 disabled:opacity-40 transition-colors"
              >
                {isBusy("workshop") ? "Creating…" : "Create one"}
              </button>
              <button
                onClick={() => handleCreate("all_workshops")}
                disabled={isBusy("all_workshops")}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60 disabled:opacity-50 transition-colors"
              >
                {isBusy("all_workshops") ? "Creating…" : "All workshops"}
              </button>
            </div>
          </div>
        </Card>

        <Card title="Tutorial codes" accent="amber" icon={<GraduationCap className="h-4 w-4" />}>
          <p className="text-xs text-slate-400 mb-3">
            Codes that give access to a specific tutorial.
          </p>
          <div className="flex flex-col gap-3">
            <div className="relative">
              <select
                className="w-full appearance-none bg-slate-900 border border-slate-700/60 rounded-md pl-3 pr-9 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                defaultValue=""
                onChange={(e) => handleCountChange("tutorial", e.target.value)}
              >
                <option value="" disabled>
                  Select tutorial…
                </option>
                {tutorials.map((tutorial: any) => (
                  <option key={tutorial} value={tutorial}>
                    Tutorial {tutorial}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleCreate("tutorial")}
                disabled={isBusy("tutorial") || !counts.tutorial}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500/20 text-amber-200 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-40 transition-colors"
              >
                {isBusy("tutorial") ? "Creating…" : "Create one"}
              </button>
              <button
                onClick={() => handleCreate("all_tutorials")}
                disabled={isBusy("all_tutorials")}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60 disabled:opacity-50 transition-colors"
              >
                {isBusy("all_tutorials") ? "Creating…" : "All tutorials"}
              </button>
            </div>
          </div>
        </Card>

        <Card title="Exhibitor pass" accent="rose" icon={<Presentation className="h-4 w-4" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PerksList items={["Exhibitor area (4 days)", "Technical Program"]} />
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Create</span>
                <CountSelect
                  value={counts.exhibitor}
                  onChange={(n) => handleCountChange("exhibitor", n)}
                  options={COUNT_OPTIONS}
                  accent="rose"
                />
                <span className="text-slate-300">passes</span>
              </div>
              <button
                onClick={() => handleCreate("exhibitor")}
                disabled={isBusy("exhibitor")}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-rose-500/20 text-rose-200 border border-rose-500/30 hover:bg-rose-500/30 disabled:opacity-50 transition-colors"
              >
                {isBusy("exhibitor") ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </Card>

        <Card title="Industry day pass" accent="cyan" icon={<Factory className="h-4 w-4" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PerksList items={["Industry Day"]} />
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Create</span>
                <CountSelect
                  value={counts.industryDay}
                  onChange={(n) => handleCountChange("industryDay", n)}
                  options={COUNT_OPTIONS}
                  accent="cyan"
                />
                <span className="text-slate-300">passes</span>
              </div>
              <button
                onClick={() => handleCreate("industryDay")}
                disabled={isBusy("industryDay")}
                className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-50 transition-colors"
              >
                {isBusy("industryDay") ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </Card>
      </div>

      <CodesModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        codes={generatedCodes}
      />
    </>
  );
}
